import * as React from 'react';
import {Link, ScrollElement, scroller, scrollSpy} from 'react-scroll';

import {useMountEffect} from 'common/onboarding/utils';

import {SetupGuideContext} from '../types';
import styles from '../index.module.styl';

const SCROLL_CONTAINER_ID = `scrollable`;

export {scrollSpy};

export const scrollToStep = (step, options = {skipAnimation: false}) => {
  const {skipAnimation} = options;
  scroller.scrollTo(step, {
    duration: 1000,
    delay: 0,
    offset: 0,
    smooth: skipAnimation ? false : `easeInOutQuart`,
    containerId: SCROLL_CONTAINER_ID,
  });
};

export const Scrollable = React.forwardRef(
  (props: {children; className}, ref: React.MutableRefObject<HTMLDivElement>) => {
    return (
      <div className={props.className} id={SCROLL_CONTAINER_ID} ref={ref}>
        {props.children}
      </div>
    );
  },
);

const Element = (props) => {
  const {active, children, parentBindings, viewSpy, spyCallback, ...rest} = props;

  const [isInView, setIsInView] = React.useState(false);
  const observerRef = React.useRef(null);
  const elementRef = React.useRef(null);

  useMountEffect(() => {
    if (viewSpy) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && elementRef.current) {
            setIsInView(true);
            observerRef.current.unobserve(elementRef.current);
          }
        },
        {
          root: document.querySelector(`#${SCROLL_CONTAINER_ID}`),
          rootMargin: `-50% 0% -50% 0%`,
        },
      );
    }
  });

  React.useEffect(() => {
    observerRef.current?.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [observerRef.current]);

  React.useEffect(() => {
    if (viewSpy && spyCallback && isInView) {
      spyCallback();
    }
  }, [isInView, viewSpy, spyCallback]);

  const asActive = (viewSpy && isInView) || active;

  return (
    <div {...rest} style={{opacity: asActive ? 1 : 0.2, pointerEvents: !asActive && `none`}} ref={elementRef}>
      {children}
    </div>
  );
};

export const ScrollableElement = ScrollElement(Element);

export const ScrollableLink = (props) => {
  const {setStep} = React.useContext(SetupGuideContext);

  const {targetStep, onSetActive, children, ...rest} = props;

  return (
    <Link
      activeClass={styles.current}
      onSetActive={() => setStep(targetStep)}
      to={targetStep}
      spy
      containerId={SCROLL_CONTAINER_ID}
      {...rest}
    >
      {children}
    </Link>
  );
};
